import Button from "@components/ui/button";
import DialogBase from "@components/ui/dialog-base";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { notionApi } from "@/shared/services";
import { layoutsPublishApi } from "@/shared/services/layouts-publish";

const DIALOG_ID = "notion-deployment";

// The layouts publish/promote routes reject a bad export with an actionable
// reason (unresolvable root folder, an export that would render an empty
// Layouts tab). Surface it instead of a generic failure toast.
const serverMessage = (error: unknown, fallback: string) => {
  const message = (
    error as { response?: { data?: { message?: string } } } | undefined
  )?.response?.data?.message;
  return message ? `${fallback}: ${message}` : fallback;
};

const NotionDeployment = () => {
  const dispatch = useAppDispatch();
  const controllerRef = useRef<AbortController | null>(null);
  const [isPublishingTemplates, setIsPublishingTemplates] = useState(false);
  const [isPublishingLinks, setIsPublishingLinks] = useState(false);
  const [isPublishingStoryExamples, setIsPublishingStoryExamples] =
    useState(false);
  const [isPublishingLayouts, setIsPublishingLayouts] = useState(false);
  const [isPromotingLayouts, setIsPromotingLayouts] = useState(false);

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handlePublishTemplates = async () => {
    setIsPublishingTemplates(true);
    controllerRef.current?.abort();
    controllerRef.current = new AbortController();
    try {
      await notionApi.publishTemplates({
        signal: controllerRef.current.signal,
      });
      toast.success(
        "We will send you an email when the templates are published",
      );
    } catch {
      toast.error("Failed to publish templates");
    }
    setIsPublishingTemplates(false);
  };

  const handlePublishLinks = async () => {
    setIsPublishingLinks(true);
    controllerRef.current?.abort();
    controllerRef.current = new AbortController();
    try {
      await notionApi.publishLinks({ signal: controllerRef.current.signal });
      toast.success("Successfully published marketing links");
    } catch {
      toast.error("Failed to publish marketing links");
    }
    setIsPublishingLinks(false);
  };

  const handlePublishStoryExamples = async () => {
    setIsPublishingStoryExamples(true);
    controllerRef.current?.abort();
    controllerRef.current = new AbortController();
    try {
      await notionApi.publishStoryExamples({
        signal: controllerRef.current.signal,
      });
    } catch {
      toast.error("Failed to publish story examples");
    }
    setIsPublishingStoryExamples(false);
  };

  // Publishes the whole global slides library (not Notion-sourced) to the
  // TEST channel: draft catalog/index only test-channel orgs (the global
  // library org) generate with. Production is untouched until promote.
  const handlePublishLayoutsToTest = async () => {
    const ok = window.confirm(
      "Publish the global slides library to the TEST channel? " +
        "Only test orgs generate with it — production is not affected.",
    );
    if (!ok) return;
    setIsPublishingLayouts(true);
    controllerRef.current?.abort();
    controllerRef.current = new AbortController();
    try {
      const result = await layoutsPublishApi.publish(
        { target: "test" },
        controllerRef.current.signal,
      );
      toast.success(
        `Published ${result.layouts} layouts (${result.newLayouts} new) to the test channel — ` +
          `Layouts tab: ${result.categories} categories, ${result.renderableSlides} slides`,
      );
    } catch (error) {
      toast.error(serverMessage(error, "Failed to publish layouts to test"));
    }
    setIsPublishingLayouts(false);
  };

  // Copies the tested draft over the live catalog + layout-source.json.
  // The previous production pair is archived server-side for rollback.
  const handlePromoteLayouts = async () => {
    const ok = window.confirm(
      "Promote the TEST layouts to PRODUCTION? " +
        "This replaces layout-source.json and the AI generation catalog for everyone.",
    );
    if (!ok) return;
    setIsPromotingLayouts(true);
    controllerRef.current?.abort();
    controllerRef.current = new AbortController();
    try {
      const result = await layoutsPublishApi.promote(
        controllerRef.current.signal,
      );
      toast.success(
        `Promoted ${result.layouts} layouts to production` +
          (result.sourcePromoted ? "" : " (layout-source unchanged — no draft)"),
      );
    } catch (error) {
      toast.error(
        serverMessage(
          error,
          "Failed to promote layouts — was anything published to test?",
        ),
      );
    }
    setIsPromotingLayouts(false);
  };

  return (
    <DialogBase>
      <DialogBase.Header className="tw-font-sans tw-text-base tw-font-semibold tw-text-blue-base">
        Notion Deployment
      </DialogBase.Header>
      <DialogBase.Content className="tw-grid tw-grid-cols-2 tw-gap-3">
        <Button
          variant="secondary"
          onClick={handlePublishTemplates}
          className={cn(
            "tw-gap-1.5",
            isPublishingTemplates && "tw-cursor-default",
          )}
        >
          {isPublishingTemplates && (
            <Spinner className="tw-size-3 tw-border-blue" />
          )}
          {isPublishingTemplates
            ? "Publishing templates..."
            : "Publish templates"}
        </Button>
        <Button
          variant="secondary"
          onClick={handlePublishLinks}
          className={cn("tw-gap-1.5", isPublishingLinks && "tw-cursor-default")}
        >
          {isPublishingLinks && (
            <Spinner className="tw-size-3 tw-border-blue" />
          )}
          {isPublishingLinks ? "Publishing..." : "Publish marketing links"}
        </Button>
        <Button
          variant="secondary"
          onClick={handlePublishStoryExamples}
          className={cn(
            "tw-gap-1.5",
            isPublishingStoryExamples && "tw-cursor-default",
          )}
        >
          {isPublishingStoryExamples && (
            <Spinner className="tw-size-3 tw-border-blue" />
          )}
          {isPublishingStoryExamples ? "Publishing..." : "Publish examples"}
        </Button>
        <Button
          variant="secondary"
          onClick={handlePublishLayoutsToTest}
          className={cn(
            "tw-gap-1.5",
            isPublishingLayouts && "tw-cursor-default",
          )}
        >
          {isPublishingLayouts && (
            <Spinner className="tw-size-3 tw-border-blue" />
          )}
          {isPublishingLayouts
            ? "Publishing layouts..."
            : "Publish layouts (test)"}
        </Button>
        <Button
          variant="secondary"
          onClick={handlePromoteLayouts}
          className={cn(
            "tw-gap-1.5",
            isPromotingLayouts && "tw-cursor-default",
          )}
        >
          {isPromotingLayouts && (
            <Spinner className="tw-size-3 tw-border-blue" />
          )}
          {isPromotingLayouts
            ? "Promoting layouts..."
            : "Promote layouts to prod"}
        </Button>
      </DialogBase.Content>
      <DialogBase.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Go back
        </Button>
      </DialogBase.Footer>
    </DialogBase>
  );
};

NotionDeployment.id = DIALOG_ID;
export default NotionDeployment;
